"use client";

import { useState, type FormEvent } from "react";
import type { FormState } from "@/modules/shared/forms";

/**
 * A form that submits to a command the host supplies (R22 T14).
 *
 * **This exists so the business components could move without being rewritten.**
 * They were built around `useActionState` and a `<form action={…}>`: the fields
 * are named, the hidden inputs carry ids, and a submit button's own
 * `name`/`value` is sometimes the payload. All of that is fine markup and none
 * of it was Next-specific; the only Next-specific part was that a server action
 * received the `FormData` automatically.
 *
 * So `FormData` stays the shape, and the *destination* becomes a prop: a
 * function that turns the same `FormData` into a JSON request. Ten components
 * crossed hosts keeping their markup, their field names and their error
 * handling, which is the difference between relocating them and rewriting them.
 * The Next host is gone; the shape stays, because what it buys now is a design
 * layer that holds no transport.
 *
 * It is not a replacement for `useActionState`: there is no progressive
 * enhancement here, because the static app has no server to post to without
 * JavaScript. Every one of these screens is behind a login and inside a
 * dashboard, so there was never a no-JavaScript path through them to protect.
 */
export type FormAction = (form: FormData) => Promise<FormState>;

export interface FormActionState {
  /** What the component renders: banner, field errors, echoed values. */
  readonly state: FormState;
  readonly pending: boolean;
  /** Hand this to `<form onSubmit={…}>`. */
  readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  /** For the few places that need to clear a banner by hand. */
  readonly setState: (state: FormState) => void;
}

export function useFormAction(action: FormAction): FormActionState {
  const [state, setState] = useState<FormState>({});
  const [pending, setPending] = useState(false);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    // **The submitter's own name/value has to be added back.** A browser
    // includes the button that submitted the form in the submission; the
    // `FormData(form)` constructor does not. Several of these forms rely on it
    // (one form, a row of buttons, each carrying the id it acts on), and losing
    // it would silently submit without the one field that says *which*.
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    if (
      (submitter instanceof HTMLButtonElement ||
        submitter instanceof HTMLInputElement) &&
      submitter.name
    ) {
      form.set(submitter.name, submitter.value);
    }

    setPending(true);
    setState({});
    void action(form)
      .then(setState)
      .finally(() => setPending(false));
  };

  return { state, pending, onSubmit, setState };
}

/**
 * Turn a `FormData` into a plain object for a JSON request.
 *
 * Repeated keys collapse to an array, because that is what a checkbox group is
 * and the opening-hours grid is exactly that: dropping all but the last box
 * would close a clinic four days a week.
 */
export function formToJson(form: FormData): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of form.entries()) {
    const text = typeof value === "string" ? value : "";
    const existing = out[key];
    if (existing === undefined) out[key] = text;
    else if (Array.isArray(existing)) existing.push(text);
    else out[key] = [existing, text];
  }
  return out;
}
